import { type AxiosError, isAxiosError } from 'axios'
import { BAD_REQUEST_CODE, UNAUTHORIZED_CODE } from '@/config/httpCodes.ts'
import type ErrorHandler from '@/models/ErrorHandler.ts'
import type { ApiValidationDetails, IApiValidationError } from '@/typings/api'
import type { ToastServiceMethods } from 'primevue'
import { DocumentToAcceptError } from '@/models/DocumentToAcceptError.ts'

export const extractValidationErrors = (
    axiosError: AxiosError,
    errorHandler?: ErrorHandler,
): ApiValidationDetails => {
    if (axiosError.status !== BAD_REQUEST_CODE) {
        return {}
    }

    const validationError = axiosError as AxiosError<IApiValidationError>

    try {
        if (typeof validationError.response?.data?.details !== 'object') {
            errorHandler?.captureError(
                new Error('Invalid backend validation error details'),
                'extractValidationErrors',
                undefined,
                {
                    backendErrorDetails: JSON.stringify(validationError.response?.data?.details),
                    errorStatus: axiosError.status,
                },
            )

            return {}
        }

        return validationError.response.data.details
    } catch (err) {
        errorHandler?.captureError(err, 'extractValidationErrors')

        return {}
    }
}

export const onApiErrorHandler = ({
    err,
    action,
    errorHandler,
    errorTitle,
    defaultErrorMessage,
    toast,
}: {
    err: unknown
    action: string
    errorHandler: ErrorHandler | undefined
    errorTitle: string
    defaultErrorMessage: string
    toast: ToastServiceMethods
}) => {
    if (isAxiosError(err) && err.response?.status === UNAUTHORIZED_CODE) {
        // Unauthorized response is handled in axios interceptor
        return
    }

    let errorMessage = defaultErrorMessage

    if (isAxiosError(err) && err.response?.data?.message) {
        errorMessage = err.response.data.message
    }

    if (err instanceof DocumentToAcceptError) {
        errorMessage = err.message
    }

    errorHandler?.captureError(err, action, undefined, { message: errorMessage })

    if (isAxiosError(err) && err.code === 'ECONNABORTED') {
        // Timeout error toast is handled in axios interceptor
        return
    }

    toast.add({
        life: 5000,
        detail: errorMessage,
        summary: errorTitle,
        severity: 'error',
    })
}
